import React, { ReactNode, useEffect, useRef, useState } from "react"
import cx from "classnames"
import "./Carousel.css"

const Carousel = ({
    children,
    timeout,
    itemsPerPage = 1,
    contentClassName = "",
    innerPaginator = false,
    className = "",
    onPageChange,
}: {
    children: ReactNode
    contentClassName?: string
    className?: string
    timeout: number
    itemsPerPage: number
    innerPaginator?: boolean
    onPageChange?: (page: number) => void
}) => {
    const [page, setPage] = useState(1)
    const interval = useRef<NodeJS.Timer>()
    const isMounted = useRef(false)

    const childrenArray = React.Children.toArray(children)
    const [animationClass, setanimationClass] = useState<"slide-in-right" | "slide-in-left">("slide-in-right")
    // Se incrementa al hacer clic en un dot para que el efecto del intervalo se reinicie y la página
    // elegida dure el `timeout` completo.
    const [timerReset, setTimerReset] = useState(0)

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true
            return
        }
        onPageChange?.(page)
    }, [page])

    useEffect(() => {
        if (!childrenArray.length) return

        const totalPages = Math.ceil(childrenArray.length / itemsPerPage)

        interval.current = setInterval(() => {
            setPage(prev => {
                const next = prev >= totalPages ? 1 : prev + 1
                setanimationClass(prev >= totalPages ? "slide-in-left" : "slide-in-right")
                return next
            })
        }, timeout)

        return () => clearInterval(interval.current)
    }, [childrenArray.length, itemsPerPage, timeout, timerReset])


    useEffect(() => {
        const totalPages = Math.ceil(childrenArray.length / itemsPerPage)
        if (page > totalPages) setPage(1)
    }, [childrenArray.length, itemsPerPage])


    return (
        <div className={cx("flex-carousel", className)}>
            <div className={cx("flex-carousel__content", contentClassName)}>
                {childrenArray
                    .filter((child, index) => {
                        const startIndex = (page - 1) * itemsPerPage
                        const endIndex = startIndex + itemsPerPage
                        return index >= startIndex && index < endIndex
                    })
                    // El key incluye la página para que el wrapper se remonte en cada cambio y la animación
                    // CSS se dispare siempre; con solo el índice React reutiliza el nodo y la página "salta".
                    .map((child, index) => (
                        <div key={`${page}-${index}`} className={animationClass}>
                            {child}
                        </div>
                    ))}
            </div>
            <div className={`flex-carousel__pages ${innerPaginator ? "inner" : ""}`}>
                {Array.from({ length: Math.ceil(childrenArray.length / itemsPerPage) }).map((_i, index) => (
                    <div
                        key={index}
                        className={`flex-carousel__page ${index + 1 === page ? "active" : ""}`}
                        onClick={() => {
                            setTimerReset((prev) => prev + 1)
                            // Clic en la página actual: solo se reinicia el temporizador; cambiar la clase
                            // reiniciaría la animación en sentido contrario.
                            if (index + 1 === page) return
                            setPage(index + 1)
                            setanimationClass(index + 1 > page ? "slide-in-right" : "slide-in-left")
                            if (onPageChange) onPageChange(index + 1)
                        }}
                    ></div>
                ))}
            </div>
        </div>
    )
}

export default Carousel
